import { Box, Typography } from '@mui/material'
import DateInput from '../../../components/editors/input/DateInput'
import { DateVO } from '../../../../domain/value-object/DateVO'
import { DateTermVO } from '../../../../domain/value-object/DateTermVO'
import { colorPalette } from '../../../style/colorPalette'
import { useCallback } from 'react'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import { Button } from '../../../components/buttons'

type SearchConditionDateProps = {
  value: DateTermVO | undefined
  title: string
  onChange: (value: DateTermVO | undefined) => void
  isBlankSearchEnabled: boolean
  blank?: boolean
  onChangeBlank?: (value: boolean) => void
}

const SearchConditionDate = ({
  value,
  title,
  onChange,
  isBlankSearchEnabled,
  blank,
  onChangeBlank,
}: SearchConditionDateProps) => {
  return (
    <Box
      sx={{
        border: `1px solid ${colorPalette.monotone[2]}`,
        borderRadius: '4px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        padding: '5px 10px',
        height: '100%',
      }}
    >
      <Typography
        sx={{
          color: colorPalette.monotone[4],
          minWidth: '80px',
          marginRight: '10px',
        }}
      >
        {title}
      </Typography>
      <SearchConditionDateCell
        value={value}
        onChange={onChange}
        isBlankSearchEnabled={isBlankSearchEnabled}
        blank={blank}
        onChangeBlank={onChangeBlank}
      />
    </Box>
  )
}

type SearchConditionDateCellProps = {
  value: DateTermVO | undefined
  onChange: (value: DateTermVO | undefined) => void
  isBlankSearchEnabled: boolean
  blank?: boolean
  onChangeBlank?: (value: boolean) => void
}

const SearchConditionDateCell = ({
  value,
  onChange,
  isBlankSearchEnabled: isBlankSearchEnabled,
  blank,
  onChangeBlank,
}: SearchConditionDateCellProps) => {
  const clearFromDate = useCallback(() => {
    if (!value?.to) {
      onChange(undefined)
      return
    }
    const newValue = {
      ...value,
      from: undefined,
    }
    onChange(newValue)
  }, [onChange, value])

  const clearToDate = useCallback(() => {
    if (!value?.from) {
      onChange(undefined)
      return
    }
    const newValue = {
      ...value,
      to: undefined,
    }
    onChange(newValue)
  }, [onChange, value])

  const clear = useCallback(() => {
    onChange(undefined)
  }, [onChange])

  const onChangeFrom = useCallback(
    (v: DateVO | undefined) => {
      if (!v && !value?.to) {
        onChange(undefined)
        return
      }
      const newValue = {
        ...value,
        from: v,
      }
      onChange(newValue)
    },
    [onChange, value]
  )
  const onChangeTo = useCallback(
    (v: DateVO | undefined) => {
      if (!v && !value?.from) {
        onChange(undefined)
        return
      }
      const newValue = {
        ...value,
        to: v,
      }
      onChange(newValue)
    },
    [onChange, value]
  )

  return (
    <Box
      sx={{
        marginLeft: '20px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      <Box sx={{ maxWidth: '110px' }}>
        <DateInput
          value={value && value.from}
          onChange={onChangeFrom}
          isBlankSearchEnabled={isBlankSearchEnabled}
          placeholder={'YYYY/MM/DD'}
          onChangeBlank={onChangeBlank}
          checkedBlank={blank}
          onChangedSwitchButton={clear}
        />
      </Box>
      <Button
        colorPattern="monotone"
        sx={{
          border: 'none',
          background: colorPalette.monotone[0],
          width: '10px',
          height: '24px',
          borderRadius: '15px',
          cursor: 'pointer',
        }}
        onClick={clearFromDate}
      >
        <ClearRoundedIcon
          sx={{
            color: colorPalette.monotone[4],
          }}
        />
      </Button>
      <Typography sx={{ margin: 'auto 10px', color: colorPalette.monotone[3] }}>
        ～
      </Typography>
      <Box sx={{ maxWidth: '110px' }}>
        <DateInput
          value={value && value.to}
          onChange={onChangeTo}
          isBlankSearchEnabled={isBlankSearchEnabled}
          placeholder={'YYYY/MM/DD'}
          onChangeBlank={onChangeBlank}
          checkedBlank={blank}
          onChangedSwitchButton={clear}
        />
      </Box>
      <Button
        colorPattern="monotone"
        sx={{
          border: 'none',
          background: colorPalette.monotone[0],
          width: '10px',
          height: '24px',
          borderRadius: '15px',
          cursor: 'pointer',
        }}
        onClick={clearToDate}
      >
        <ClearRoundedIcon
          sx={{
            color: colorPalette.monotone[4],
          }}
        />
      </Button>
    </Box>
  )
}

export default SearchConditionDate
